import { ImageLinkProps } from '@awaze/design-system.components.image-link'
import { LinkProps } from '@awaze/design-system.components.link'
import { MegaMenuProps } from '@awaze/design-system.compositions.mega-menu'
import {
  NavigationItemType,
  NavigationItemProps,
} from '@awaze/design-system.compositions.responsive-header'

import {
  IImageLinkFields,
  ILinkFields,
  IMegaMenuFields,
  IMegaMenuLeftPaneFields,
  IMegaMenuRightPaneFields,
  IMenuItemFields,
} from '../../../@types/generated/contentful-global'
import { Entry } from 'contentful'

export const unwrapLink = (
  link: Entry<ILinkFields>,
): Omit<LinkProps, 'children'> => {
  return {
    title: link.fields?.displayText,
    href: link.fields?.href ?? '',
    openInNewTab: link.fields?.openInNew,
    rel: link.fields?.rel,
  }
}

export const unwrapImageLink = (
  link: Entry<IImageLinkFields>,
): ImageLinkProps => {
  return {
    title: link.fields?.text ?? '',
    link: {
      href: link.fields?.url ?? '',
    },
    image: link.fields?.image?.fields.file.url
      ? {
          src: link.fields?.image?.fields.file.url,
          alt: link.fields?.image?.fields.description ?? '',
        }
      : undefined,
  }
}

export const unwrapMegaMenuLeftSection = (
  leftPane: Entry<IMegaMenuLeftPaneFields>,
): MegaMenuProps['leftSection'] | undefined => {
  if (!leftPane?.fields) {
    return undefined
  }
  const { title, links, footerLinkText, footerLinkUrl, isMultiColumn } =
    leftPane.fields

  return {
    title,
    footerLink:
      footerLinkText && footerLinkUrl
        ? {
            title: footerLinkText,
            href: footerLinkUrl,
          }
        : undefined,
    links: links
      ?.map((link) => ({ link: unwrapLink(link) }))
      .filter((link) => link.link && link.link.title),
    isMultiColumns: isMultiColumn?.valueOf(),
  }
}

export const unwrapMegaMenuRightSection = (
  rightPane: Entry<IMegaMenuRightPaneFields>,
): MegaMenuProps['rightSection'] | undefined => {
  if (!rightPane?.fields) {
    return undefined
  }
  const { title, imageOrLink, footerLinkText, footerLinkUrl, showAsALinks } =
    rightPane.fields

  return {
    title,
    footerLink:
      footerLinkText && footerLinkUrl
        ? {
            title: footerLinkText,
            href: footerLinkUrl,
          }
        : undefined,
    isImageLinks: showAsALinks?.valueOf(),
    links: imageOrLink
      ?.map(unwrapImageLink)
      .filter((link) => link && link.title),
  }
}

export const unwrapMegaMenu = (
  megaMenu: Entry<IMegaMenuFields>,
): MegaMenuProps => {
  const { id, displayText, leftPane, rightPane } = megaMenu.fields
  return {
    id: id || '',
    displayText: displayText || '',
    leftSection: leftPane && unwrapMegaMenuLeftSection(leftPane),
    rightSection: rightPane && unwrapMegaMenuRightSection(rightPane),
  }
}

export const unwrapMenuItem = (
  item: Entry<IMenuItemFields>,
): NavigationItemProps => {
  const {
    type,
    link,
    highlight,
    title,
    icon,
    subMenu,
    megaMenu,
    enableMobileLoginIcon,
    displayFor,
    showUserEmail,
  } = item.fields

  return {
    id: link.fields.id || '',
    type,
    link: {
      displayText: link.fields.displayText || '',
      href: link.fields.href,
      id: link.fields.id || '',
      openInNew: link.fields.openInNew,
      rel: link.fields.rel,
    },
    highlight,
    title,
    icon,
    subMenu: subMenu && unwrapMenu(subMenu),
    megaMenu: megaMenu && unwrapMegaMenu(megaMenu),
    enableMobileLoginIcon,
    displayFor,
    showUserEmail,
  }
}

export const unwrapMenu = (
  menu: Entry<IMenuItemFields>[],
): NavigationItemType[] => (menu ? menu.map(unwrapMenuItem) : [])
